import { BrandLogo } from "@/components/BrandLogo";
import { useLocale as __useLocale } from "@/context/LocaleContext";
import { useLocale, LanguagePicker } from "@/context/LocaleContext";
import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getCategories } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Heart, Menu, Search, ShoppingBag, ChevronRight, User } from "lucide-react";

import PortalEntries from "@/components/PortalEntries";

const EXTRA_NAV = [
  { to: "/stil-danismani", label: "Shule Stil Danışmanı" },
  { to: "/mobil-uygulamalar", label: "Mobil uygulamalar" },
  { to: "/hediye", label: "Hediye" },
  { to: "/shule-club", label: "Shule Club" },
  { to: "/magazalar", label: "Mağazalar" },
  { to: "/bayilik", label: "Bayilik" },
  { to: "/hakkimizda", label: "Hakkımızda" },
];

export function Header({compact=false}) {
  const { t: __t } = __useLocale();
  const { t } = useLocale();
  const { cartCount, favorites, setCartOpen } = useStore();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [categoryState, setCategoryState] = useState("loading");
  const [categoryAttempt, setCategoryAttempt] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    setCategoryState("loading");
    getCategories().then((items) => {
      if (!active) return;
      if (!Array.isArray(items) || items.some((item) => !item || typeof item.name !== "string" || !item.name.trim() || typeof item.slug !== "string" || !item.slug.trim())) throw new Error("Invalid categories");
      setCategories(items);
      setCategoryState(items.length ? "ready" : "empty");
    }).catch(() => {
      if (active) { setCategories([]); setCategoryState("error"); }
    });
    return () => { active = false; };
  }, [categoryAttempt]);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 glass-header border-b border-shule-line">
      <div className="container-shule relative flex items-center justify-between h-16 lg:h-[76px]">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2" aria-label={t("Menüyü aç")}>
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent aria-describedby={undefined} side="left" className="w-[85vw] max-w-sm bg-shule-paper border-shule-line p-0 flex flex-col">
            <SheetTitle className="sr-only">{t("Menü")}</SheetTitle>
            <div className="flex items-center justify-between px-5 h-16 shrink-0 border-b border-shule-line">
              <Link to="/" onClick={() => setMobileOpen(false)} aria-label={t("Shule ana sayfa")} data-testid="mobile-menu-logo" className="inline-flex">
                <BrandLogo sizes="82px" alt={__t("Shule")} className="h-9 w-auto" />
              </Link>
            </div>
            <nav aria-label={t("Alışveriş menüsü")} className="px-5 py-6 flex flex-col gap-1 overflow-y-auto min-h-0 flex-1">
              <p className="eyebrow mb-3">{t("Kategoriler")}</p>
              <Link data-testid="mobile-nav-all-products" to="/kategori/tumu" onClick={() => setMobileOpen(false)} className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                {t("Tüm Ürünler")}<ChevronRight className="w-4 h-4 text-shule-gold" />
              </Link>
              {categoryState === "loading" && <p role="status" className="py-3 text-sm">{t("Kategoriler yükleniyor…")}</p>}
              {categoryState === "empty" && <p role="status" className="py-3 text-sm">{t("Henüz yayımlanmış kategori bulunmuyor.")}</p>}
              {categoryState === "error" && <div role="alert" className="py-3 text-sm"><p>{t("Kategoriler yüklenemedi.")}</p><button type="button" className="min-h-[44px] underline" onClick={() => setCategoryAttempt(value => value + 1)}>{t("Yeniden dene")}</button></div>}
              {categories.map((c) => (
                <Link key={c.slug} data-testid={`mobile-nav-${c.slug}`} to={`/kategori/${encodeURIComponent(c.slug)}`} onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                  {t(c.name)}
                  <ChevronRight className="w-4 h-4 text-shule-gold" />
                </Link>
              ))}
              <p className="eyebrow mt-6 mb-3">{t("Keşfet")}</p>
              {EXTRA_NAV.map((n) => (
                <Link key={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} to={n.to} onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                  {t(n.label)}
                  <ChevronRight className="w-4 h-4 text-shule-gold" />
                </Link>
              ))}
              <Link data-testid="mobile-nav-account" to={user ? "/hesap" : "/giris"} onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                {__t(user ? t("Hesabım") : t("Giriş Yap"))}
                <ChevronRight className="w-4 h-4 text-shule-gold" />
              </Link>
            </nav>
          </SheetContent>
        </Sheet>

        <Link to="/" data-testid="header-logo" className="absolute left-1/2 -translate-x-1/2 select-none">
          <BrandLogo sizes="(min-width: 1024px) 109px, 91px" alt={__t("Shule Accessories — Her Anında Sen")} className="h-10 lg:h-12 w-auto" />
        </Link>

        <div className="flex items-center gap-0.5 ml-auto">
          <button data-testid="header-search-button" onClick={() => navigate("/ara")} className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label={t("Ara")}>
            <Search className="w-5 h-5" />
          </button>
          <Link to={user ? "/hesap" : "/giris"} data-testid="header-account-button"
            className="min-w-[44px] min-h-[44px] hidden sm:flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label={t("Hesap")}>
            {user ? (
              <span data-testid="header-account-initial" className="w-7 h-7 rounded-full bg-shule-goldlight border border-shule-gold/40 flex items-center justify-center text-xs font-bold text-shule-coffee">
                {(user.name || user.email || "S")[0].toUpperCase()}
              </span>
            ) : (
              <User className="w-5 h-5" />
            )}
          </Link>
          <Link to="/favoriler" data-testid="header-favorites-button" className="relative min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label={t("Favoriler")}>
            <Heart className="w-5 h-5" />
            {favorites.length > 0 && (
              <span data-testid="favorites-count-badge" className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-shule-rose text-white text-[10px] font-bold flex items-center justify-center">
                {__t(favorites.length)}
              </span>
            )}
          </Link>
          <button data-testid="header-cart-button" onClick={() => setCartOpen(true)} className="relative min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label={t("Sepet")}>
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-shule-coffee text-shule-paper text-[10px] font-bold flex items-center justify-center">
                {__t(cartCount)}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="border-t border-shule-line/60"><div className="container-shule flex flex-wrap items-center justify-between gap-2 py-2">{!compact&&<PortalEntries user={user}/>}<div data-testid="header-language-controls" className="ml-auto"><LanguagePicker /></div></div></div>
      <nav className="hidden lg:block border-t border-shule-line/60" aria-label={t("Kategori menüsü")}>
        <div className="container-shule flex items-center justify-center gap-7">
          <NavLink data-testid="nav-all-products" to="/kategori/tumu" className="text-[13px] font-semibold min-h-[44px] inline-flex items-center">{t("Tüm Ürünler")}</NavLink>
          <NavLink data-testid="nav-yeni-sezon" to="/koleksiyon/gun-isigi"
            className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
            {t("Yeni Sezon")}
          </NavLink>
          {categories.map((c) => (
            <NavLink key={c.slug} data-testid={`nav-${c.slug}`} to={`/kategori/${encodeURIComponent(c.slug)}`}
              className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
              {t(c.name)}
            </NavLink>
          ))}
          <NavLink data-testid="nav-hediye" to="/hediye"
            className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
            {t("Hediye")}
          </NavLink>
          <NavLink data-testid="nav-shule-club" to="/shule-club"
            className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>{__t("Shule Club")}</NavLink>
        </div>
      </nav>
    </header>
  );
}
